import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Typography,
} from "@mui/material";
import { grey, yellow } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SlideAnimationContext } from "contexts/SlideAnimation";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BentoCategory,
  OrganizationVaProfile,
  VaNicheSpecificDetails,
  VaNicheType,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import ExcludeIncludeFilters from "features/Auth/Onboarding/RecommendationForm/ExcludeIncludeFilters";
import { FREE_NICHES } from "utils/influencer";
import { makeDeepCopy } from "utils/updateLocalState";

import { vaSelfServeNavigate } from "../helpers";
import Niche from "./Niche";

interface Props {
  categoriesToPickFrom: BentoCategory[];
  numOfNiche: number;
  numOfTemplates: number;
  saveOrgVaProfile: (
    organizationVaProfile: OrganizationVaProfile,
    excludeIncludeFilterHasChanged?: boolean,
    moveNext?: boolean,
  ) => void;
  organizationVaProfile: OrganizationVaProfile | null;
  setOrganizationVaProfile: Dispatch<
    SetStateAction<OrganizationVaProfile | null>
  >;
  loading: boolean;
}

const NichePick = ({
  categoriesToPickFrom,
  numOfNiche,
  numOfTemplates,
  saveOrgVaProfile,
  organizationVaProfile,
  setOrganizationVaProfile,
  loading,
}: Props) => {
  const location = useLocation();
  const slideContext = useContext(SlideAnimationContext);
  const { setAlert } = useContext(AlertContext);
  const [niches, setNiches] = useState<VaNicheSpecificDetails[]>([]);
  const navigate = useNavigate();
  const { outreachTemplates } = useContext(OutreachTemplatesContext);

  const [excludeIncludeFilterHasChanged, setExcludeIncludeFilterHasChanged] =
    useState(false);
  const [personalizeOutreach, setPersonalizeOutreach] = useState(false);

  const handleNext = async (e: CustomEvent) => {
    e.preventDefault();
    const isValid = niches.every((niche) => niche?.bento_category_id);
    if (!isValid) {
      setAlert("Please fill out all the niche", "warning");
      return;
    }
    const copy = makeDeepCopy(organizationVaProfile || {});
    copy.niches = niches;
    copy.personalizeOutreach = personalizeOutreach;
    setOrganizationVaProfile(copy);

    saveOrgVaProfile(copy, excludeIncludeFilterHasChanged, false);

    if (slideContext) {
      await slideContext.slideOutUp();
      slideContext.slideInUp();
    }

    if (numOfTemplates) {
      navigate(
        `/${routes.templatesCopyWriterSelfServeOnboarding}${location.search}`,
      );
      return;
    }
    if (outreachTemplates && outreachTemplates?.length > 0) {
      navigate(
        `/${routes.templatesFromScratchSelfServeOnboarding}${location.search}`,
      );
    } else {
      navigate(
        `/${routes.templatesMethodSelfServeOnboarding}${location.search}`,
      );
    }
  };

  const handleBack = async () => {
    if (slideContext) {
      await slideContext.slideOutDown();
      slideContext.slideInDown();
    }
    navigate(
      `/${vaSelfServeNavigate(location.pathname, "back")}${location.search}`,
    );
  };

  const initializeNiches = () => {
    const initialNiches: VaNicheSpecificDetails[] = [];
    if (organizationVaProfile?.niches) {
      for (const niche of organizationVaProfile.niches) {
        if (niche.bento_category_id) {
          initialNiches.push(niche);
        }
      }
    }
    if (initialNiches.length > 0) {
      setNiches(initialNiches);
      return;
    }

    for (let i = 0; i < numOfNiche + FREE_NICHES; i++) {
      initialNiches.push({
        name: `Niche ${i + 1}`,
        notes: "",
        type: VaNicheType.general,
      });

      setNiches(initialNiches);
    }
  };

  const initializePersonalizeOutreach = () => {
    if (organizationVaProfile?.personalizeOutreach) {
      setPersonalizeOutreach(true);
    }
  };

  useEffect(() => {
    initializeNiches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numOfNiche]);

  useEffect(() => {
    initializePersonalizeOutreach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationVaProfile?.personalizeOutreach]);

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center", mt: 0 }}>
        Step 1: Select Your Niches
      </Typography>

      <form onSubmit={handleNext}>
        {Array.from({ length: niches?.length }, (_, index) => (
          <Niche
            key={index}
            niche={niches[index]}
            niches={niches}
            setNiches={setNiches}
            index={index}
            categoriesToPickFrom={categoriesToPickFrom}
          />
        ))}

        <Typography variant="h6" sx={{ fontSize: 14 }}>
          More customizations
        </Typography>

        <ExcludeIncludeFilters
          setExcludeIncludeFilterHasChanged={setExcludeIncludeFilterHasChanged}
          hideSecondaryText
        />

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: 14, mb: 1 }}>
            AI Personalization
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={personalizeOutreach}
                onChange={(e) => setPersonalizeOutreach(e.target.checked)}
                icon={<Box component="i" className="fa-regular fa-square" />}
                checkedIcon={
                  <Box component="i" className="fa-solid fa-check-square" />
                }
              />
            }
            label="Use AI to personalize my outreach emails"
          />

          <FormHelperText sx={{ color: grey[700] }}>
            Your email will be automatically personalized to better match each
            brand's industry, improving relevance and reducing mismatches.{" "}
            <Box
              component="span"
              sx={{ color: yellow[800] }}
              className="fa-solid fa-triangle-exclamation"
            />{" "}
            However, AI may occasionally include inaccurate details or make the
            email sound less natural than your original template.
          </FormHelperText>
        </Box>

        <Grid item xs="auto" container columnGap={1} sx={{ mt: 4 }}>
          <Grid item xs="auto">
            <Button disableElevation onClick={handleBack}>
              <Box
                component="i"
                className="fa-solid fa-arrow-left"
                sx={{ mr: 1 }}
              />
              Back
            </Button>
          </Grid>
          <Grid item xs></Grid>
          <Grid item xs="auto">
            <Button disableElevation type="submit" disabled={loading}>
              Next
              <Box
                component="i"
                className="fa-solid fa-arrow-right"
                sx={{ ml: 1 }}
              />
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default NichePick;
